import { createPortal } from 'react-dom'
import { forwardRef, useEffect, HTMLAttributes } from 'react'
import styled, { css } from 'styled-components'
import { animated, useTransition, useSpring } from '@react-spring/web'
import { isMobile } from 'react-device-detect'
import { transparentize } from 'polished'
import { useDrag } from '@use-gesture/react'

// Custom DialogOverlay - replaces @reach/dialog
interface DialogOverlayProps extends HTMLAttributes<HTMLDivElement> {
  isOpen?: boolean
  onDismiss?: () => void
  initialFocusRef?: React.RefObject<HTMLElement | null>
}

const DialogOverlay = forwardRef<HTMLDivElement, DialogOverlayProps>(
  ({ isOpen, onDismiss, children, initialFocusRef, ...props }, ref) => {
    useEffect(() => {
      if (isOpen && initialFocusRef?.current) {
        initialFocusRef.current.focus()
      }
    }, [isOpen, initialFocusRef])

    useEffect(() => {
      if (!isOpen) return
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onDismiss?.()
      }
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }, [isOpen, onDismiss])

    if (!isOpen) return null

    return createPortal(
      <div ref={ref} data-reach-dialog-overlay="" onClick={onDismiss} {...props}>
        {children}
      </div>,
      document.body
    )
  }
)

DialogOverlay.displayName = 'DialogOverlay'

// Custom DialogContent - replaces @reach/dialog
const DialogContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, onClick, ...props }, ref) => (
    <div
      ref={ref}
      data-reach-dialog-content=""
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
      role="dialog"
      aria-modal="true"
      {...props}
    >
      {children}
    </div>
  )
)

DialogContent.displayName = 'DialogContent'

const AnimatedDialogOverlay = animated(DialogOverlay)
const StyledDialogOverlay = styled(AnimatedDialogOverlay)`
  &[data-reach-dialog-overlay] {
    z-index: 2;
    background-color: transparent;
    overflow: hidden;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${({ theme }) => theme.modalBG};

    /* Base overlay styles (previously from @reach/dialog/styles.css) */
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
`

const AnimatedDialogContent = animated(DialogContent)
const StyledDialogContent = styled(({ minHeight: _minHeight, maxHeight: _maxHeight, mobile: _mobile, isOpen: _isOpen, ...rest }: {
  minHeight?: number | false
  maxHeight?: number
  mobile?: boolean
  isOpen?: boolean
  [key: string]: unknown
}) => (
  <AnimatedDialogContent {...rest} />
)).attrs({
  'aria-label': 'dialog'
})`
  &[data-reach-dialog-content] {
    margin: 0 0 2rem 0;
    border: 1px solid ${({ theme }) => theme.bg1};
    background-color: ${({ theme }) => theme.bg1};
    box-shadow: 0 4px 8px 0 ${({ theme }) => transparentize(0.95, theme.shadow1)};
    padding: 0px;
    width: 50vw;
    overflow: hidden;

    align-self: ${({ mobile }) => (mobile ? 'flex-end' : 'center')};

    max-width: 420px;
    ${({ maxHeight }) =>
      maxHeight &&
      css`
        max-height: ${maxHeight}vh;
      `}
    ${({ minHeight }) =>
      minHeight &&
      css`
        min-height: ${minHeight}vh;
      `}
    display: flex;
    border-radius: 20px;
    ${({ theme }) => theme.mediaWidth.upToMedium`
      width: 65vw;
      margin: 0;
    `}
    ${({ theme, mobile }) => theme.mediaWidth.upToSmall`
      width:  85vw;
      ${mobile &&
        css`
          width: 100vw;
          border-radius: 20px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        `}
    `}
  }
`

interface ModalProps {
  isOpen: boolean
  onDismiss: () => void
  minHeight?: number | false
  maxHeight?: number
  initialFocusRef?: React.RefObject<HTMLElement | null>
  children?: React.ReactNode
}

export default function Modal({
  isOpen,
  onDismiss,
  minHeight = false,
  maxHeight = 50,
  initialFocusRef,
  children
}: ModalProps) {
  const transitions = useTransition(isOpen, {
    config: { duration: 200 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  })

  const [{ y }, api] = useSpring(() => ({ y: 0, config: { mass: 1, tension: 210, friction: 20 } }))

  const bind = useDrag(
    ({ down, movement: [, my], velocity: [, vy], direction: [, dy] }) => {
      api.start({ y: down ? my : 0 })
      if (my > 300 || (vy > 3 && dy > 0)) {
        onDismiss()
      }
    },
    { axis: 'y', filterTaps: true }
  )

  return (
    <>
      {transitions((style, item) =>
        item ? (
          <StyledDialogOverlay style={style} onDismiss={onDismiss} initialFocusRef={initialFocusRef} isOpen={item}>
            <StyledDialogContent
              {...(isMobile
                ? {
                    ...bind(),
                    style: { transform: y.to((yVal) => `translateY(${yVal > 0 ? yVal : 0}px)`) }
                  }
                : {})}
              aria-label="dialog content"
              minHeight={minHeight}
              maxHeight={maxHeight}
              mobile={isMobile}
            >
              {/* prevents the automatic focusing of inputs on mobile by the reach dialog */}
              {!initialFocusRef && isMobile ? <div tabIndex={1} /> : null}
              {children}
            </StyledDialogContent>
          </StyledDialogOverlay>
        ) : null
      )}
    </>
  )
}
